import { useEffect, useRef, useState } from 'react';
import { MAX_TITLE_LENGTH } from '@planning-session/shared';
import styles from './Room.module.css';

export interface StoryBarProps {
  readonly title: string;
  readonly canEdit: boolean;
  /**
   * Incremented by the host pressing "Next story". A new round is always
   * untitled, so this both clears the draft and takes focus — without it, a
   * field that was focused before the empty title arrived keeps the old story.
   */
  readonly focusSignal: number;
  onCommit: (title: string) => void;
}

/**
 * The story being estimated. The host types freely and the value is pushed on
 * blur or Enter — a keystroke-per-message would be noise on the wire.
 */
export function StoryBar({ title, canEdit, focusSignal, onCommit }: StoryBarProps) {
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  const editingRef = useRef(false);

  // Accept remote changes unless this user is mid-edit.
  useEffect(() => {
    if (!editingRef.current) setDraft(title);
  }, [title]);

  useEffect(() => {
    if (focusSignal === 0 || !canEdit) return;
    editingRef.current = false;
    setDraft('');
    inputRef.current?.focus();
  }, [focusSignal, canEdit]);

  if (!canEdit) {
    return (
      <div className={styles.story}>
        <span className={styles.storyLabel}>Now estimating</span>
        <p className={`${styles.storyText} ${title ? '' : styles.storyEmpty}`}>
          {title || 'The host has not named a story yet'}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.story}>
      <label className={styles.storyLabel} htmlFor="story-title">
        Now estimating
      </label>
      <input
        id="story-title"
        ref={inputRef}
        className={styles.storyInput}
        value={draft}
        maxLength={MAX_TITLE_LENGTH}
        placeholder="What are we sizing?"
        onFocus={() => {
          editingRef.current = true;
        }}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          editingRef.current = false;
          if (draft !== title) onCommit(draft);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          } else if (event.key === 'Escape') {
            setDraft(title);
            event.currentTarget.blur();
          }
        }}
      />
    </div>
  );
}
